import { useState } from 'react';

import { config } from '~app/config';

import type { OrderTableData } from '~widgets/orders-table/model/orders-table-data';

import { useGetOrderByIdQuery } from '~entities/order';

import { Loader } from '~shared/ui/loader';
import { MoneyPh } from '~shared/ui/money-ph';

import Button from '@platform-ui/button';
import { Int24Success } from '@platform-ui/iconsPack';

import { format } from 'date-fns';

import { statusToTextMap } from '../../lib/columns';

export const OrderDetails = ({ order }: { order: OrderTableData }) => {
  const {
    data: fullOrder,
    isLoading,
    isSuccess,
  } = useGetOrderByIdQuery(order.orderId);

  const dateObj = new Date(order.orderTimestampUTC);
  const dateFormatted = format(dateObj, 'dd-MM-yyyy, hh:mm a');
  const link = `${config.onlineHost}/order/${order.orderId}`;
  const [isCopied, setIsCopied] = useState(false);
  const handleCopyClick = async () => {
    await navigator.clipboard.writeText(link);
    setIsCopied(true);
  };
  const handleCopyBlur = () => {
    if (isCopied) {
      setTimeout(() => setIsCopied(false), 200);
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-4 pt-2 text-sm text-base-primary">
      <section>
        <h2 className="mb-3 mt-0 text-2xl font-bold">
          {order.customerFullName ?? 'Order details'}
        </h2>
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {order.shopOrderId && (
            <li className="flex justify-between">
              <span className="text-base">Order number</span>
              <span className="line-clamp-1 max-w-[180px] text-base">
                {order.shopOrderId}
              </span>
            </li>
          )}
          <li className="flex justify-between">
            <span className="text-base">Status</span>
            <span className="text-base">
              {statusToTextMap[order.orderStatus]}
            </span>
          </li>
          <li className="flex justify-between">
            <span className="text-base">Date&nbsp;&&nbsp;time</span>
            <span className="text-base">{dateFormatted}</span>
          </li>
          <li className="flex justify-between gap-10">
            <span className="text-base">Store&nbsp;name</span>
            <div
              className={`
                line-clamp-1 max-w-full overflow-hidden text-ellipsis
                whitespace-nowrap text-base
              `}
            >
              {order.posName}
            </div>
          </li>
        </ul>
      </section>
      <section>
        <h2 className="m-0 mb-3 text-2xl font-bold">Purchased items</h2>

        <ul
          className={`
            relative m-0 flex min-h-[24px] list-none flex-col gap-3 p-0
          `}
        >
          {isLoading && <Loader />}
          {isSuccess &&
            fullOrder.items.map((item) => (
              <li key={item.name} className="flex justify-between">
                <span className="text-base">
                  {item.name}&nbsp;{item.isUsed && '(Used)'}
                </span>
                <div className="flex text-base">
                  {item.quantity}&nbsp;x&nbsp;
                  <MoneyPh value={item.price} />
                </div>
              </li>
            ))}
          <li className="flex justify-between">
            <span className="text-base font-semibold">Total</span>
            <span className="text-base font-semibold">
              <MoneyPh value={order.totalAmount} />
            </span>
          </li>
        </ul>
      </section>
      <section className="m-auto mt-4">
        {isCopied ? (
          <div
            onBlur={handleCopyBlur}
            onMouseOut={handleCopyBlur}
            className={`
              box-border flex h-[56px] w-[176px] cursor-default items-center
              justify-center gap-2 rounded-lg border border-solid
              border-[#0B92D3D] px-[35px] pb-[14px] pt-[15px] font-body
              text-[15px] text-[#00A127]
            `}
          >
            <Int24Success theme="green" /> Copied
          </div>
        ) : (
          <Button size="l" onClick={handleCopyClick}>
            Copy order link
          </Button>
        )}
      </section>
    </div>
  );
};
